import { Icon as IconType } from '@tabler/icons-react';
import * as Yup from 'yup';
import { FormInputData, ValidFormData } from 'hooks/useTypedForm';
import _ from 'lodash';
import { terminationTypeOptionsExpanded } from 'types/part';
import {
  OrgPartIcons,
  OrgPartType,
  OrgPartTypeLabels,
} from 'types/clientV2/parts';
import { UNLIMITED_QUANTITY_STRING } from 'constants/procurementConstants';
import {
  DimensionClassOptions,
  UnitOfMeasureDetails,
} from 'types/unitsOfMeasure';

export type PartTypeProps = {
  id: string;
  label: string;
  description: string;
  Icon: IconType;
};

export const PartTypesDefinitions: {
  [key in OrgPartType]: PartTypeProps;
} = {
  [OrgPartType.OTS]: {
    id: OrgPartType.OTS,
    label: OrgPartTypeLabels[OrgPartType.OTS],
    description: 'This is a non-custom part that you buy from a supplier.',
    Icon: OrgPartIcons[OrgPartType.OTS],
  },
  [OrgPartType.CUSTOM]: {
    id: OrgPartType.CUSTOM,
    label: OrgPartTypeLabels[OrgPartType.CUSTOM],
    description: 'This is a custom part that a supplier makes for you.',
    Icon: OrgPartIcons[OrgPartType.CUSTOM],
  },
  [OrgPartType.ASSEMBLY]: {
    id: OrgPartType.ASSEMBLY,
    label: OrgPartTypeLabels[OrgPartType.ASSEMBLY],
    description: 'This is a part assembled from other parts.',
    Icon: OrgPartIcons[OrgPartType.ASSEMBLY],
  },
  [OrgPartType.SERVICE]: {
    id: OrgPartType.SERVICE,
    label: OrgPartTypeLabels[OrgPartType.SERVICE],
    description:
      'This is a service or other non-part item that you purchase from suppliers.',
    Icon: OrgPartIcons[OrgPartType.SERVICE],
  },
  [OrgPartType.NON_STOCKED]: {
    id: OrgPartType.NON_STOCKED,
    label: OrgPartTypeLabels[OrgPartType.NON_STOCKED],
    description:
      "This is a material that you don't want to track specific inventory records for.",
    Icon: OrgPartIcons[OrgPartType.NON_STOCKED],
  },
  [OrgPartType.BUNDLED]: {
    id: OrgPartType.BUNDLED,
    label: OrgPartTypeLabels[OrgPartType.BUNDLED],
    description:
      'This is a grouping of parts that you buy together, but that should be tracked separately.',
    Icon: OrgPartIcons[OrgPartType.BUNDLED],
  },
};

export const OrgPartFormConstants = {
  partType: {
    id: 'partType' as const,
    label: 'Part Type',
  },
  orgPartNumber: {
    id: 'customId' as const,
    label: 'Part Number',
  },
  revision: {
    id: 'revision' as const,
    label: 'Revision',
  },
  link: {
    id: 'link' as const,
    label: 'Link',
  },
  mfg: {
    id: 'manufacturer' as const,
    label: 'Manufacturer',
  },
  mpn: {
    id: 'mpn' as const,
    label: 'Manufacturer Part Number',
  },
  description: {
    id: 'description' as const,
    label: 'Description',
  },
  linkMatchedPart: {
    id: 'linkMatchedPart' as const,
    label: 'Link Matched Part',
  },
  classification: {
    id: 'classification' as const,
    label: 'Part Category',
  },
  dimensionClass: {
    id: 'dimensionClass' as const,
    label: 'Unit of Measure',
  },
  preferredUnit: {
    id: 'preferredUnit' as const,
    label: 'Preferred Unit',
  },
  isElectronicComponent: {
    id: 'isElectronicComponent' as const,
    label: 'Electronic Component',
  },
  mountType: {
    id: 'terminationType' as const,
    label: 'Mount Type',
  },
  packageType: {
    id: 'package' as const,
    label: 'Package Type',
  },
  msl: {
    id: 'msl' as const,
    label: 'MSL',
  },
  fileUpload: { id: 'fileUpload' as const, label: 'Upload Documents' },
  publicPart: { id: 'publicPartId' as const, label: 'Public Part' },
  heroImageUrl: { id: 'heroImageUrl' as const, label: 'Hero Image' },
  specs: { id: 'specs' as const, label: 'Specs' },
  stockDocument: { id: 'stockDocumentId' as const, label: 'Stock Document' },
  scrapedFields: { id: 'scrapedFields' as const, label: 'Scraped Fields' },
  shipsInLead: { id: 'shipsInLead' as const, label: 'Ships in Lead' },
  sku: { id: 'sku' as const, label: 'SKU' },
  packaging: { id: 'packaging' as const, label: 'Packaging' },
  coa: { id: 'coa' as const, label: 'COA' },
  moq: { id: 'moq' as const, label: 'MOQ' },
  multiple: { id: 'multiple' as const, label: 'Multiple' },
  maximum: { id: 'maximum' as const, label: 'Maximum' },
  prices: { id: 'prices' as const, label: 'Prices' },
  orgSupplier: { id: 'orgSupplier' as const, label: 'Supplier' },
  supplierQuoteOpen: {
    id: 'supplierQuoteOpen' as const,
    label: 'Add Supplier Pricing',
  },
  lastScrapedAt: { id: 'lastScrapedAt' as const, label: 'Last Scraped At' },
  dimensionValue: { id: 'dimensionValue' as const, label: 'Unit Quantity' },
  dimensionUnit: { id: 'dimensionUnit' as const, label: 'Unit' },
};

export const orgPartValidationSchema = Yup.object({
  [OrgPartFormConstants.partType.id]: Yup.string(),
  [OrgPartFormConstants.orgPartNumber.id]: Yup.string().nullable(),
  [OrgPartFormConstants.revision.id]: Yup.string().nullable(),
  [OrgPartFormConstants.link.id]: Yup.string()
    .nullable()
    .trim()
    .max(2048, 'Link must be 2048 characters or fewer')
    .url('Please enter a valid URL'),
  [OrgPartFormConstants.heroImageUrl.id]: Yup.string()
    .nullable()
    .trim()
    .max(2048, 'Link must be 2048 characters or fewer')
    .url('Please enter a valid URL'),
  [OrgPartFormConstants.mfg.id]: Yup.string().when(
    [OrgPartFormConstants.partType.id],
    {
      is: (partType: string) => partType === OrgPartType.CUSTOM,
      then: Yup.string().nullable(),
      otherwise: Yup.string()
        .required(`${OrgPartFormConstants.mfg.label} is required`)
        .typeError(`${OrgPartFormConstants.mfg.label} is required`),
    }
  ),
  [OrgPartFormConstants.mpn.id]: Yup.string().when(
    [OrgPartFormConstants.partType.id, OrgPartFormConstants.orgPartNumber.id],
    {
      is: (partType: string, orgPartNumber: string) =>
        partType === OrgPartType.CUSTOM && !!orgPartNumber,
      then: Yup.string().nullable(),
      otherwise: Yup.string().required(
        `${OrgPartFormConstants.mpn.label} is required`
      ),
    }
  ),
  [OrgPartFormConstants.description.id]: Yup.string().nullable(),
  [OrgPartFormConstants.linkMatchedPart.id]: Yup.boolean(),
  [OrgPartFormConstants.classification.id]: Yup.string()
    .nullable()
    .typeError(`Select a valid ${OrgPartFormConstants.classification.label}`),
  [OrgPartFormConstants.dimensionClass.id]: Yup.string()
    .oneOf(Object.values(DimensionClassOptions))
    .typeError(`Select a valid ${OrgPartFormConstants.dimensionClass.label}`)
    .default(DimensionClassOptions.QUANTITY),
  [OrgPartFormConstants.preferredUnit.id]: Yup.string()
    .when([OrgPartFormConstants.dimensionClass.id], (dimensionClass) =>
      Yup.string()
        .oneOf(
          Object.values(UnitOfMeasureDetails)
            .filter((unit) => unit.dimensionClass === dimensionClass)
            .map((unit) => unit.value)
        )
        .required(`${OrgPartFormConstants.preferredUnit.label} is required`)
        .typeError(
          `Unit must match ${OrgPartFormConstants.dimensionClass.label}`
        )
    )
    .typeError(`Select a valid ${OrgPartFormConstants.preferredUnit.label}`)
    .required(`${OrgPartFormConstants.preferredUnit.label} is required`),
  [OrgPartFormConstants.isElectronicComponent.id]: Yup.boolean(),
  [OrgPartFormConstants.mountType.id]: Yup.string().when(
    [
      OrgPartFormConstants.partType.id,
      OrgPartFormConstants.isElectronicComponent.id,
      OrgPartFormConstants.linkMatchedPart.id,
    ],
    {
      is: (
        partType: string,
        isElectronicComponent: boolean,
        linkMatchedPart: boolean
      ) =>
        partType !== OrgPartType.SERVICE &&
        isElectronicComponent &&
        !linkMatchedPart,
      then: Yup.string()
        .oneOf(
          _.map(Object.values(terminationTypeOptionsExpanded), 'value'),
          `${OrgPartFormConstants.mountType.label} must be one of: ${_.map(
            terminationTypeOptionsExpanded,
            'label'
          ).join(', ')}`
        )
        .required(`${OrgPartFormConstants.mountType.label} is required`),
      otherwise: Yup.string().nullable(),
    }
  ),
  [OrgPartFormConstants.packageType.id]: Yup.string().nullable(),
  [OrgPartFormConstants.msl.id]: Yup.string().nullable(),
  [OrgPartFormConstants.publicPart.id]: Yup.string()
    .nullable(true)
    .transform((currentValue, originalValue) =>
      originalValue === '' ? null : currentValue
    ),
  [OrgPartFormConstants.specs.id]: Yup.object().nullable(),
  [OrgPartFormConstants.stockDocument.id]: Yup.string().nullable(),
  [OrgPartFormConstants.scrapedFields.id]: Yup.array()
    .of(Yup.string())
    .nullable(),
  [OrgPartFormConstants.orgSupplier.id]: Yup.string()
    .nullable()
    .typeError('Supplier is required'),
  [OrgPartFormConstants.moq.id]: Yup.number().nullable().min(0),
  [OrgPartFormConstants.multiple.id]: Yup.number().nullable().min(1),
  [OrgPartFormConstants.maximum.id]: Yup.mixed()
    .test(
      'is-number-or-unlimited',
      'Maximum must be a number or "Unlimited"',
      (value) =>
        value === null ||
        value === undefined ||
        typeof value === 'number' ||
        !!_.toNumber(value) ||
        value === UNLIMITED_QUANTITY_STRING
    )
    .nullable(true),
  [OrgPartFormConstants.prices.id]: Yup.array()
    .of(
      Yup.object().shape({
        to: Yup.number()
          .min(0)
          .nullable()
          .transform((value, originalValue) =>
            typeof originalValue === 'string' && originalValue.trim() === ''
              ? null
              : value
          ),
        unitPrice: Yup.number()
          .min(0)
          .nullable()
          .transform((value, originalValue) =>
            typeof originalValue === 'string' && originalValue.trim() === ''
              ? null
              : value
          ),
      })
    )
    .compact()
    .nullable(),
  [OrgPartFormConstants.shipsInLead.id]: Yup.number().min(0).nullable(),
  [OrgPartFormConstants.packaging.id]: Yup.string().nullable(),
  [OrgPartFormConstants.coa.id]: Yup.string().nullable(),
  [OrgPartFormConstants.sku.id]: Yup.string().nullable(),
  [OrgPartFormConstants.supplierQuoteOpen.id]: Yup.boolean(),
  [OrgPartFormConstants.lastScrapedAt.id]: Yup.string().nullable(),
  [OrgPartFormConstants.dimensionValue.id]: Yup.number().nullable().min(0),
  [OrgPartFormConstants.dimensionUnit.id]: Yup.string().nullable(),
});

export type OrgPartValidationSchemaFormData = ValidFormData<
  typeof orgPartValidationSchema
>;

export type OrgPartFormInputData = FormInputData<
  typeof orgPartValidationSchema
>;

type OrgPartValidationSchemaFormDataKeys =
  keyof OrgPartValidationSchemaFormData;

const isElectronicFields = [
  OrgPartFormConstants.mountType.id,
  OrgPartFormConstants.packageType.id,
  OrgPartFormConstants.msl.id,
];

export const commonFields = [
  OrgPartFormConstants.orgPartNumber.id,
  OrgPartFormConstants.mfg.id,
  OrgPartFormConstants.mpn.id,
  OrgPartFormConstants.description.id,
  OrgPartFormConstants.classification.id,
  OrgPartFormConstants.isElectronicComponent.id,
  OrgPartFormConstants.link.id,
  OrgPartFormConstants.heroImageUrl.id,
  OrgPartFormConstants.specs.id,
  OrgPartFormConstants.dimensionClass.id,
  OrgPartFormConstants.preferredUnit.id,
];

export const supplierQuoteFields = [
  OrgPartFormConstants.shipsInLead.id,
  OrgPartFormConstants.sku.id,
  OrgPartFormConstants.packaging.id,
  OrgPartFormConstants.coa.id,
  OrgPartFormConstants.moq.id,
  OrgPartFormConstants.multiple.id,
  OrgPartFormConstants.maximum.id,
  OrgPartFormConstants.prices.id,
  OrgPartFormConstants.orgSupplier.id,
];

export const formKeysForPartType: Record<
  OrgPartType,
  OrgPartValidationSchemaFormDataKeys[]
> = {
  [OrgPartType.OTS]: [
    OrgPartFormConstants.publicPart.id,
    OrgPartFormConstants.link.id,
    ...commonFields,
    ...isElectronicFields,
  ],
  [OrgPartType.SERVICE]: commonFields,
  [OrgPartType.CUSTOM]: [
    OrgPartFormConstants.revision.id,
    ...commonFields,
    ...isElectronicFields,
  ],
  [OrgPartType.NON_STOCKED]: [], // Non-stocked uses either OTS or Custom, handled in preSumbit
  [OrgPartType.BUNDLED]: [],
  [OrgPartType.ASSEMBLY]: [],
};

export const OTS_WITH_PUBLIC_PART_FIELDS = [
  OrgPartFormConstants.publicPart.id,
  OrgPartFormConstants.orgPartNumber.id,
];

export const BASE_DEFAULT_VALUES: OrgPartFormInputData = {
  [OrgPartFormConstants.partType.id]: OrgPartType.OTS,
  [OrgPartFormConstants.orgPartNumber.id]: '',
  [OrgPartFormConstants.revision.id]: '',
  [OrgPartFormConstants.link.id]: '',
  [OrgPartFormConstants.mfg.id]: '',
  [OrgPartFormConstants.mpn.id]: '',
  [OrgPartFormConstants.description.id]: '',
  [OrgPartFormConstants.linkMatchedPart.id]: false,
  [OrgPartFormConstants.classification.id]: '',
  [OrgPartFormConstants.isElectronicComponent.id]: true,
  [OrgPartFormConstants.mountType.id]: '',
  [OrgPartFormConstants.packageType.id]: '',
  [OrgPartFormConstants.msl.id]: '',
  [OrgPartFormConstants.publicPart.id]: '',
  [OrgPartFormConstants.heroImageUrl.id]: null,
  [OrgPartFormConstants.supplierQuoteOpen.id]: false,
  [OrgPartFormConstants.moq.id]: 1,
  [OrgPartFormConstants.multiple.id]: 1,
};
